import React from 'react'
import { getDynamicPowerPricesVersion, getSupportedSoftwareVersionsDynamicPowerPrices } from '../../utils/price-settings'
import { useSystemInfoStorage } from 'src/Libs/system-data-handler/api/SystemDataFetching'
import SettingsFormFullVersion from './SettingsFormFullVersion'
import SettingsFormMinimalVersion from './SettingsFormMinimalVersion'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { DynamicPricesSettings } from '../../types'
import { useUserSettings } from 'src/Hooks'
import { useCheckInstallationLocation } from '../../hooks'

interface Props {
  installationId: string | undefined
  storageId: string | undefined
  dynamicPricesSettings: DynamicPricesSettings | null
  handleDynamicPricesSettingsUpdate: (newValues: DynamicPricesSettings) => Promise<void>
}

/**
 * This component renders the dynamic prices section of the system settings.
 * It decides how the form should be rendered based on multiple factors.
 */
const DynamicPowerPriceSettingsFormContainer: React.FC<Props> = ({
  installationId,
  storageId,
  dynamicPricesSettings,
  handleDynamicPricesSettingsUpdate,
}) => {
  const installationAccessContext = useInstallationAccessRightsContext()
  const { systemInfo } = useSystemInfoStorage(storageId)
  const softwareVersion = systemInfo?.storage?.info?.sw_release
  const { doesSupportFullVersion, doesSupportMinimumVersion, doesSupportFullVersionOnlyGermany } =
    getSupportedSoftwareVersionsDynamicPowerPrices(softwareVersion)

  const { countryCode, isDppCountryInstallation } = useCheckInstallationLocation()
  const hasPriceSettings = dynamicPricesSettings !== null
  const { isMinimumVersionValid, isFullVersionValid } = getDynamicPowerPricesVersion(
    doesSupportMinimumVersion,
    doesSupportFullVersion,
    doesSupportFullVersionOnlyGermany,
    hasPriceSettings,
    countryCode
  )

  const { userSettings } = useUserSettings()

  const requiresOptIn = (): boolean => {
    const isDppEnabled = dynamicPricesSettings?.dynamicPricesForWallboxesEnabled || dynamicPricesSettings?.dynamicPricesForBatteryEnabled
    const hasOptedInAlready = userSettings?.didOptInToGermanDynamicPrices ?? false

    return !isDppCountryInstallation && !isDppEnabled && !hasOptedInAlready
  }

  if (isFullVersionValid) {
    return (
      <SettingsFormFullVersion
        key={`dynamic-prices-full-version-for-wallbox-${installationId}-${storageId}`}
        installationId={installationId}
        storageId={storageId}
        dynamicPricesSettings={dynamicPricesSettings}
        handleDynamicPricesSettingsUpdate={handleDynamicPricesSettingsUpdate}
        hasWritingRights={installationAccessContext.hasInstallationWritingRights}
        isOptInRequired={requiresOptIn()}
      />
    )
  }

  if (isMinimumVersionValid) {
    return (
      <SettingsFormMinimalVersion
        key={`dynamic-prices-minimal-version-for-wallbox-${installationId}-${storageId}`}
        storageId={storageId}
        installationId={installationId}
        dynamicPricesSettings={dynamicPricesSettings}
        handleDynamicPricesSettingsUpdate={handleDynamicPricesSettingsUpdate}
        hasWritingRights={installationAccessContext.hasInstallationWritingRights}
        isOptInRequired={requiresOptIn()}
      />
    )
  }

  return null
}

export default DynamicPowerPriceSettingsFormContainer
